import { Component, Input, OnInit } from '@angular/core';
import { BaseFormComponent } from '../../../lean-shared/components/base-form/base-form.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectAdaptor } from '../../../lean-shared/components/input-control/input-control.component';
import { StringHelper } from '../../../core/helpers/strings.helper';
import { BusinessService } from '../../../core/services/business.service';
import { ModalOptionsConfig, ModalService } from '../../../lean-shared/components/nz-modal-service/modal.service';
import { BUSINESS_CATEGORY_STEPS, BusinessTypesModal } from './business-types.model';
import { AppConfig } from '../../../shared/config/app-config';
import { ModalType } from '../../../kamona/model/kamonal-model';
import { MONIEPOINT_DESIGN_TYPE } from '@mpt-fo/common/shared/constants';
import { DashboardService } from '../../../core/services/dashboard.service';
import { UtilityService } from '@mpt-fo/common/core/services/utility.service';
import { POSBusinessTypeInfo } from '@mpt-fo/common/shared/models/utility.model';
import { logger } from '@mpt-fo/common/telemetry';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import { ScreenManagerService } from '../../../core/services/screen-manager.service';
import { DataLayerService, StorefrontUploadEvents } from '../../../core/services/data-layer-service.service';

@Component({
    selector: 'app-business-types',
    templateUrl: './business-types.component.html',
    styleUrls: ['./business-types.component.scss'],
})
export class BusinessTypesComponent extends BaseFormComponent implements OnInit {
    @Input() agentId: number;

    loadingCategories = false;
    loadingSubCategories = false;
    selectedCategory: any;
    categories: SelectAdaptor[];
    subCategories: SelectAdaptor[];

    businessCategory: number;
    businessSubCategory: number;

    tempStoreFrontImgRef: string;
    storeFrontImgRef: string;
    storeFrontImg: string;

    posBusinessInfo: POSBusinessTypeInfo = null;
    errorMessage: string;
    form: FormGroup;
    currentStep = BUSINESS_CATEGORY_STEPS.LIST_CATEGORIES;
    readonly CATEGORY_STEPS = BUSINESS_CATEGORY_STEPS;
    readonly ModalTypeEnum = ModalType;
    readonly INVALID_STOREFRONT_IMAGE_CODE = '400079';
    categoriesFormList = [];

    editModeIndex = -10;

    canShowStoreFrontSample = false;
    isPendingChanges = false;

    isSavingChanges = false;
    loadingImage = false;

    isMobileView: boolean;
    showUploadImageErrorModal = false;
    uploadImageError = "Store front image is invalid"

    constructor(
        private fb: FormBuilder,
        private businessService: BusinessService,
        private modalService: ModalService,
        private config: AppConfig,
        private onboardingService: OnboardingV2Service,
        private dashboardService: DashboardService,
        private utilityService: UtilityService,
        private screenManager: ScreenManagerService,
        private dataLayerService: DataLayerService,
    ) {
        super(fb);
        this.setForm();
    }

    get maxStoreFrontFileSize() {
        return this.config.STORE_FRONT_IMAGE_MAX_SIZE;
    }

    get maxAllowedBusinessTypes() {
        return this.config.MAX_ALLOWED_BUSINESS_TYPES;
    }

    setForm() {
        this.form = this.fb.group({
            businessId: [null, [Validators.required]],
            businessTypeId: [null, [Validators.required]],
        });
        this.onCategoryChange();
    }

    ngOnInit(): void {
        this.getInitialData();
        this.getBusinessCategories();
        this.setPosBusinessInfo();

        this.isMobileView = this.screenManager.isMobile();
    }

    getInitialData() {
        if (!this.agentId) return;

        this.dashboardService
            .getBusinessCategoriesForBrm(this.agentId)
            .then((res: BusinessTypesModal) => {
                if (res?.associatedBusinessTypes?.length) {
                    res.associatedBusinessTypes.forEach((associatedBusinessType) => {
                        this.categoriesFormList.push(associatedBusinessType);
                    });
                    this.storeFrontImgRef = res.storeFrontImageReference;
                    this.fetchStoreFrontImg(this.storeFrontImgRef);
                }
            })
            .catch((err) => {
                logger.error(`Error during getting initial business categories and image reference: ${err.message}`);
            });
    }

    async setPosBusinessInfo() {
        const config = await this.utilityService.getConfig();
        this.posBusinessInfo = config.posBusinessTypeInfo;
    }

    fetchStoreFrontImg(imageReference?: string) {
        if (!imageReference) return;
        this.loadingImage = true;
        this.dashboardService
            .getFileInfo(imageReference)
            .then((res) => {
                this.storeFrontImg = res.base64File;
            })
            .catch((err) => {
                logger.error(`Error during fetching image from reference: ${err.message}`);
            })
            .finally(() => {
                this.loadingImage = false;
            });
    }

    handleNext() {
        const subCategory = this.form.get('businessTypeId').value;
        const allSubcategoryIds = this.categoriesFormList.map((category) => category.businessTypeId);

        const category = {
            name: subCategory,
            businessCategoryName: this.form.get('businessId').value,
            businessTypeId: this.subCategories.find((_subCategory) => _subCategory.label === subCategory)?.value,
        };

        const isDuplicate = allSubcategoryIds.includes(category.businessTypeId) && this.editModeIndex === -10;
        const indexOfExisting = allSubcategoryIds.indexOf(category.businessTypeId);
        if ((indexOfExisting > -1 && indexOfExisting !== this.editModeIndex) || isDuplicate) {
            this.errorMessage = 'This business type already exists. Please select a different business type.';
            return;
        }

        if (this.editModeIndex > -10) {
            this.categoriesFormList.splice(this.editModeIndex, 1, category);
            this.editModeIndex = -10;
        } else {
            this.categoriesFormList.push(category);
        }
        this.currentStep = BUSINESS_CATEGORY_STEPS.LIST_CATEGORIES;
    }

    toggleSample() {
        this.canShowStoreFrontSample = !this.canShowStoreFrontSample;
    }

    handleGetFile($event) {
        this.tempStoreFrontImgRef = $event.fileReference;
        this.isPendingChanges = true;
    }

    handleLogUploadEvent(status: 'initiated' | 'success' | 'failed', error?: Record<string, any>) {
        const payload = { business_id: this.agentId };

        if(this.tempStoreFrontImgRef) {        
            switch(status) {
                case 'initiated':
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.initiated, payload);
                    return; 
                case 'success':
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.success, payload);
                    return;
                case 'failed':
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.failed, {
                        ...payload,
                        api_error_code: error?.error?.code,
                        api_error_message: error?.error?.description,
                    });
                    return;
            }
        }
    }


    async saveBusinessTypes() {
        this.modalService.closeAll();

        this.isSavingChanges = true;
        const businessTypeIds = this.categoriesFormList.map((category) => category?.businessTypeId);

        const storeFrontImageReference = this.tempStoreFrontImgRef ?? this.storeFrontImgRef;

        if (!storeFrontImageReference) return this.alertError('Please upload a clear photo of your shopfront including the signpost.');

        this.handleLogUploadEvent('initiated');
            
        try {
            const payload = { businessId: `${this.agentId}`, businessTypeIds, storeFrontImageReference };

            await this.businessService.saveBusinessTypesInformation(payload);
            this.storeFrontImgRef = storeFrontImageReference;

            this.handleLogUploadEvent('success');


            if (this.storeFrontImgRef) {
                this.fetchStoreFrontImg(this.storeFrontImgRef);
            }
            this.isPendingChanges = false;
            this.alertSuccess('Business categorized successfully');
        } catch (err) {
            logger.error(`Error while submitting business types: ${err.message}`);

            if(err?.error?.code === this.INVALID_STOREFRONT_IMAGE_CODE) {
                this.handleInvalidStoreImageUpload(err);
            } else {
                this.alertError(err?.error?.description ?? err.message);
            }
        } finally {
            this.isSavingChanges = false;
        }
    }

    handleInvalidStoreImageUpload(error) {
        this.handleLogUploadEvent('failed', error);

        this.uploadImageError = error?.error?.description ?? "Store front image is invalid";
        this.showUploadImageErrorModal = true;
    }

    handleSubmit() {
        const posTypeId = this.posBusinessInfo.businessTypeId;
        const isPOSBankingInList = this.categoriesFormList.some((category) => category.businessTypeId === posTypeId);

        if (this.posBusinessInfo && !isPOSBankingInList) {
            return this.alertWarning();
        }

        this.saveBusinessTypes();
    }

    onCategoryChange(): void {
        this.form.get('businessId').valueChanges.subscribe((value) => {
            if (!value) {
                return;
            }
            this.selectedCategory = this.categories.find((category) => category.label === value)?.value ?? '';
            this.form.controls['businessTypeId'].setValue('');
            this.getBusinessSubCategories();
        });
    }

    getBusinessCategories() {
        this.loadingCategories = true;

        this.onboardingService
            .getBusinessCategories()
            .then((res) => {
                this.categories = res.map((category) => {
                    category['label'] = category.name;
                    category['value'] = category.id;
                    return category;
                });
                this.setBusinessCategory();
            })
            .catch((error) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
                logger.error(this.errorMessage);
                this.alertError(this.errorMessage);
            })
            .finally(() => {
                this.loadingCategories = false;
            });
    }

    getBusinessSubCategories() {
        if (!this.selectedCategory) return;

        this.loadingSubCategories = true;
        this.subCategories = [];

        this.businessService
            .getBusinessSubCategories(this.selectedCategory)
            .then((res) => {
                this.subCategories = res.map((subCategory) => {
                    subCategory['label'] = subCategory.name;
                    subCategory['value'] = subCategory.id;
                    return subCategory;
                });
                this.setBusinessSubCategory();
            })
            .catch((error) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
            })
            .finally(() => {
                this.loadingSubCategories = false;
            });
    }

    showMaxCategoriesAlert() {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'info';
        config.nzComponentParams.title = 'Selection Limit Notice';
        config.nzComponentParams.message = `You can select up to ${this.maxAllowedBusinessTypes} business categories. Need help? Contact customer support.`;
        config.nzComponentParams.callback = () => {
            this.modalService.closeAll();
        };
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        config.nzComponentParams.callbackName = 'Dismiss';
        this.modalService.alert(config);
    }

    alertSuccess(message: string) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'success';
        config.nzComponentParams.message = message;
        this.modalService.alert(config);
    }

    alertError(message: string) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'error';
        config.nzComponentParams.title = 'Something went wrong';
        config.nzComponentParams.message = message;

        this.modalService.alert(config);
    }

    alertWarning() {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'info';
        config.nzComponentParams.title = 'POS/Agency banking';
        config.nzComponentParams.message = 'Will you use this business account for POS/Agency banking activities? (e.g., cash withdrawals)';
        config.nzComponentParams.callback = () => {
            if (this.categoriesFormList.length >= this.maxAllowedBusinessTypes) {
                return this.showMaxCategoriesAlert();
            }
            this.addPOSBanking();
        };

        config.nzComponentParams.cancelCallback = () => {
            this.saveBusinessTypes();
        };
        config.nzComponentParams.cancelName = "No, I Won't";
        config.nzComponentParams.callbackName = 'Yes, I Will';
        this.modalService.alert(config);
    }

    addPOSBanking() {
        this.categoriesFormList.push({
            name: this.posBusinessInfo.businessTypeName,
            businessCategoryName: this.posBusinessInfo.categoryName,
            businessTypeId: this.posBusinessInfo.businessTypeId,
        });

        this.saveBusinessTypes();
    }

    setBusinessCategory() {
        if (this.businessCategory) {
            const object: SelectAdaptor = this.categories.find((category) => category.label == this.businessCategory);
            if (object) {
                this.form.controls['businessId'].setValue(object.value);
            }
        }
    }

    setBusinessSubCategory() {
        if (this.businessSubCategory) {
            const object: SelectAdaptor = this.subCategories.find((subCategory) => subCategory.label == this.businessSubCategory);
            if (object) {
                this.form.controls['businessTypeId'].setValue(object.value);
            }
        }
    }

    handleEditCategory(index: number) {
        if (!this.categoriesFormList?.length) return;

        this.errorMessage = '';
        this.isPendingChanges = true;
        const selected = this.categoriesFormList.find((_, categoryIndex) => categoryIndex === index);
        if (!selected) return;

        this.selectedCategory = this.categories.find((category) => category.label === selected.businessCategoryName)?.value;

        this.form = this.fb.group({
            businessId: [selected.businessCategoryName, [Validators.required]],
            businessTypeId: [selected.name, [Validators.required]],
        });

        this.editModeIndex = index;
        this.onCategoryChange();
        this.getBusinessSubCategories();

        this.loadTempImagePreview();
        this.currentStep = BUSINESS_CATEGORY_STEPS.EDIT_ADD_CATEGORY;
    }

    handleCreateCategory() {
        if (this.categoriesFormList.length >= this.maxAllowedBusinessTypes) {
            return this.showMaxCategoriesAlert();
        }

        this.setForm();
        this.errorMessage = '';
        this.isPendingChanges = true;
        this.loadTempImagePreview();
        this.currentStep = BUSINESS_CATEGORY_STEPS.EDIT_ADD_CATEGORY;
    }

    handleDeleteCategory(index: number) {
        this.isPendingChanges = true;
        this.categoriesFormList.splice(index, 1);
    }

    handleCloseAlertModal() {
        this.showUploadImageErrorModal = false;
        this.tempStoreFrontImgRef = undefined;
    }

    goToListCategories() {
        this.currentStep = BUSINESS_CATEGORY_STEPS.LIST_CATEGORIES;
    }

    loadTempImagePreview() {
        if (!this.tempStoreFrontImgRef) return;

        this.fetchStoreFrontImg(this.tempStoreFrontImgRef);
    }

    get isSubmitDisabled() {
        return !((this.storeFrontImgRef || this.tempStoreFrontImgRef) && this.categoriesFormList.length > 0 && this.isPendingChanges);
    }
}
